import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { ethers } from "ethers";
import { NETWORKID_ACTIVE, NETWORKID_ACTIVE_DEC, SERVER_URL } from "../utils/constants";
import { fetchUserInfo } from "../api/user";
import { useUserStore } from '../stores/useUserStore'
import { toast } from "react-toastify";
import axios from "axios";

// Define Wallet Context Type
interface WalletContextType {
  account: string | null;
  networkId: string | null;
  isConnected: boolean;
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
}

// Create Context
const WalletContext = createContext<WalletContextType | undefined>(undefined);

// Wallet Provider Component
export const WalletProvider = ({ children }: { children: ReactNode }) => {
  const [account, setAccount] = useState<string | null>(localStorage.getItem("walletAddress"));
  const [isConnected, setIsConnected] = useState(!!localStorage.getItem("walletAddress"));
  const [networkId, setNetworkId] = useState<string | null>(localStorage.getItem("networkId"));
  const setUser = useUserStore((s) => s.setUser);
  const reset = useUserStore((state) => state.reset);
  // Connect Wallet Function
  const connectWallet = async () => {
    if (!window.ethereum) {
      toast.error("MetaMask is not installed!");
      return;
    }

    if (typeof window.ethereum !== "undefined" && window.ethereum) {
      console.log('wallet context')
      try {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
        const chainId = await provider.send("eth_chainId", []);

        setNetworkId(chainId);
        localStorage.setItem("networkId", chainId);
        const res = await axios.post(`${SERVER_URL}/api/logger`, {
          message: 'change network',
          data: chainId
        });
        const selectedAccount = accounts[0];

        setAccount(selectedAccount);
        setIsConnected(true);
        localStorage.setItem("walletAddress", selectedAccount); // Store wallet in localStorage

        if (selectedAccount) {
          const user = await fetchUserInfo(selectedAccount);
          if(!user)
            return;

          setUser({
            account: user.address,
            balance: "0",
            referral: user.ref_id,
            rewards: user.rewards
          });


        }

        //const provider_network = new window.ethereum;



        if (chainId !== NETWORKID_ACTIVE || chainId !== NETWORKID_ACTIVE_DEC) {
          console.error(`NETWORK ERROR: ${chainId}`);
        }


      } catch (error) {
        console.error("Wallet connection failed:", error);
      }
    }


  };

  // Disconnect Wallet Function
  const disconnectWallet = () => {
    setAccount(null);
    setNetworkId(null);
    setIsConnected(false);
    localStorage.removeItem("walletAddress"); // Clear stored wallet
    localStorage.removeItem("networkId");
    reset();
  };

  useEffect(() => {
    const tryReconnect = async () => {
      const savedAccount = localStorage.getItem("walletAddress");
      const savedNetwork = localStorage.getItem("networkId");

      if (savedAccount && savedNetwork) {
        try {
          const provider = new ethers.BrowserProvider(window.ethereum);
          const accounts = await provider.send("eth_accounts", []);
          const chainId = await provider.send("eth_chainId", []);

          if (accounts.length > 0) {
            const user = await fetchUserInfo(accounts[0]);
            if(!user)
              return;
            setAccount(accounts[0]);
            setNetworkId(chainId);
            setIsConnected(true);
            setUser({
              account: user.address,
              balance: "0", // có thể load lại balance ở nơi khác
              referral: user.ref_id,
              rewards: user.rewards
            });
          }
        } catch (err) {
          console.error("❌ Failed to reconnect wallet:", err);
        }
      }
    };

    if (typeof window !== "undefined" && window.ethereum) {
      tryReconnect();
    }
  }, []);


  // Auto-reconnect when the page reloads
  useEffect(() => {
    if (typeof window.ethereum !== "undefined" && window.ethereum) {
      window.ethereum.on("accountsChanged", (accounts: string[]) => {

        const handleAccountsChange = async () => {
          if (accounts.length > 0) {
            const user = await fetchUserInfo(accounts[0]);
            if(!user)
              return;
            setUser({
              account: user.address,
              balance: "0",
              referral: user.ref_id,
              rewards: user.rewards
            });
            setAccount(accounts[0]);
            setIsConnected(true);
            localStorage.setItem("walletAddress", accounts[0]);
          } else {

            disconnectWallet();
          }
        };

        handleAccountsChange();
      });

      window.ethereum.on("chainChanged", async (chainId: string) => {
        console.log("🔄 Network changed:", chainId); // Debugging log

        setNetworkId(chainId);

        localStorage.setItem("networkId", chainId);
        const res = await axios.post(`${SERVER_URL}/api/logger`, {
          message: 'change network',
          data: chainId
        });
        // window.location.reload();
        await connectWallet();
      });
    } else {
      console.error("❌ MetaMask (window.ethereum) not found!");
    }
  }, []);

  return (
    <WalletContext.Provider value={{ account, networkId, isConnected, connectWallet, disconnectWallet }}>
      {children}
    </WalletContext.Provider>
  );
};

// Hook for Using Wallet Context in Any Component
export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
};


